import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n";

const PrivacyPolicyPage = () => {
  const { t } = useLanguage();
  const sections = t("privacyPolicy.sections");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 bg-gradient-hero pointer-events-none" />
      <div className="fixed inset-0 grid-pattern opacity-30 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-background/80 backdrop-blur-xl border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center justify-between h-16 lg:h-20">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="relative">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary to-accent opacity-50 blur-lg group-hover:opacity-70 transition-opacity" />
                </div>
                <span className="text-xl font-display font-bold text-foreground">
                  Lumiere
                </span>
              </Link>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/" className="flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </Button>
            </nav>
          </div>
        </header>

        {/* Page content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* Page title */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground mb-4">
              {t("privacyPolicy.title")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("privacyPolicy.lastUpdated")}
            </p>
          </div>

          {/* Intro */}
          <div className="glass-card rounded-2xl p-6 sm:p-8 mb-10">
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("privacyPolicy.intro")}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <section
                key={index}
                className="p-6 sm:p-8 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/20 transition-colors duration-300"
              >
                <h2 className="text-xl sm:text-2xl font-display font-semibold text-foreground mb-4">
                  {section.title}
                </h2>
                <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {/* Back to home */}
          <div className="mt-16 text-center">
            <Button variant="ghost" asChild>
              <Link to="/" className="flex items-center gap-2 mx-auto">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </main>

        {/* Minimal footer */}
        <footer className="border-t border-border/50 bg-card/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} Lumiere.{" "}
                {t("footer.copyright")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("footer.madeWith")} <span className="text-red-500">♥</span>{" "}
                {t("footer.forCommunity")}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
